type IProductGroupMasterRestriction = {
  sales_restricted?: boolean
  allowed_sales_customers?: { id: string }[]
}

type IProductGroupMasterForBanner = IProductGroupMasterRestriction & {
  sales_restricted?: boolean
  allowed_sales_customers?: { id: string; name?: string; branch?: { name?: string } }[]
  code?: string
}

type IProductWithPgm = {
  id?: string
  stock_code?: string
  branch?: { name?: string } | null
  product_group?: {
    product_group_master?: IProductGroupMasterForBanner
  }
}

const excludeProductMasterSearchBranches = ["test", "demo", "training", "hub", "ssp"]

function productMasterSearchProducts(products: IProductWithPgm[]): IProductWithPgm[] {
  return products.filter(
    p =>
      p.branch?.name && !excludeProductMasterSearchBranches.includes(p.branch.name.toLowerCase()),
  )
}

type IRestrictedBranchProduct = {
  id: string
  stock_code?: string
  branch?: { name?: string } | null
}

type IBypassCustomer = { id: string; name?: string; branch?: { name?: string } }

export type ISalesRestrictionBannerInfo = {
  searchReason: string
  fullyRestricted: boolean
  /** Some branch products are sales-restricted while others are not. */
  mixedAccess: boolean
  subtitleSummary: string
  pgmCodes: string[]
  bypassCustomers: IBypassCustomer[]
  restrictedProducts: IRestrictedBranchProduct[]
}

/** Skeem fetch attributes for product master sales-restriction banner data. */
export const productMasterSalesRestrictionFetchAttributes = [
  { name: "customers" },
  {
    name: "products",
    attributes: [
      "stock_code",
      "legacy_stock_code",
      { name: "branch", attributes: ["name"] },
      {
        name: "product_group",
        attributes: [
          {
            name: "product_group_master",
            attributes: [
              "code",
              "sales_restricted",
              {
                name: "allowed_sales_customers",
                attributes: ["name", { name: "branch", attributes: ["name"] }],
              },
            ],
          },
        ],
      },
    ],
  },
] as const

function toRestrictedBranchProducts(products: IProductWithPgm[]): IRestrictedBranchProduct[] {
  return products
    .filter(p => p.id)
    .map(p => ({
      id: p.id!,
      stock_code: p.stock_code,
      branch: p.branch,
    }))
}

export function getSalesRestrictionBannerInfo(master: {
  customers: { id: string }[]
  products: IProductWithPgm[]
}): ISalesRestrictionBannerInfo | null {
  if (master.products.length === 0) {
    return null
  }

  const restrictedProducts = master.products.filter(
    p => p.product_group?.product_group_master?.sales_restricted === true,
  )
  if (restrictedProducts.length === 0) {
    return null
  }

  const mixedAccess = restrictedProducts.length < master.products.length
  const restrictedBranchProducts = toRestrictedBranchProducts(restrictedProducts)

  const pgmCodes = new Set<string>()
  const bypassCustomersById = new Map<string, IBypassCustomer>()

  for (const product of restrictedProducts) {
    const pgm = product.product_group?.product_group_master
    if (pgm?.code) {
      pgmCodes.add(pgm.code)
    }
    pgm?.allowed_sales_customers?.forEach(c => {
      if (!bypassCustomersById.has(c.id)) {
        bypassCustomersById.set(c.id, c)
      }
    })
  }

  const codes = [...pgmCodes]
  const codesText = codes.length > 0 ? codes.join(", ") : "unknown"
  const bypassCustomers = [...bypassCustomersById.values()]

  if (mixedAccess) {
    return {
      searchReason: `Mixed access — ${restrictedProducts.length} of ${master.products.length} branch products are sales-restricted via PGM (${codesText})`,
      fullyRestricted: false,
      mixedAccess: true,
      subtitleSummary: `Some branch products belong to sales-restricted product group master(s) (${codesText}) while others do not — availability varies by branch.`,
      pgmCodes: codes,
      bypassCustomers,
      restrictedProducts: restrictedBranchProducts,
    }
  }

  const fullyRestricted = bypassCustomers.length === 0

  if (fullyRestricted) {
    return {
      searchReason: `All branch products belong to sales-restricted product group master(s) (${codesText}) with no bypass customers`,
      fullyRestricted: true,
      mixedAccess: false,
      subtitleSummary: `All branch products belong to sales-restricted product group master(s) (${codesText}) with no bypass customers.`,
      pgmCodes: codes,
      bypassCustomers: [],
      restrictedProducts: restrictedBranchProducts,
    }
  }

  return {
    searchReason: `Sales restricted via PGM (${codesText}) — only bypass-list customers can find this in search`,
    fullyRestricted: false,
    mixedAccess: false,
    subtitleSummary: `Sales restricted via product group master(s) (${codesText}) — only bypass-list customers can find this in search.`,
    pgmCodes: codes,
    bypassCustomers,
    restrictedProducts: restrictedBranchProducts,
  }
}

/** Compute Algolia customerIds facet for a product master, merging nopl and sales-restriction rules. */
export function computeAlgoliaCustomerIds(master: {
  customers: { id: string }[]
  products: IProductWithPgm[]
}): string[] {
  const noplCustomerIds = master.customers.map(c => c.id)
  if (noplCustomerIds.length > 0) {
    return noplCustomerIds
  }

  if (master.products.length === 0) {
    return ["none"]
  }

  const products = productMasterSearchProducts(master.products)
  if (products.length === 0) {
    return ["none"]
  }

  const restrictedProducts = products.filter(
    p => p.product_group?.product_group_master?.sales_restricted === true,
  )
  if (restrictedProducts.length === 0) {
    return ["none"]
  }

  let intersection: Set<string> | null = null
  for (const product of restrictedProducts) {
    const bypassIds: string[] =
      product.product_group?.product_group_master?.allowed_sales_customers?.map(c => c.id) ?? []
    if (bypassIds.length === 0) {
      return ["__restricted__"]
    }
    const bypassSet = new Set(bypassIds)
    if (intersection === null) {
      intersection = bypassSet
    } else {
      const next = new Set<string>()
      for (const id of intersection) {
        if (bypassSet.has(id)) {
          next.add(id)
        }
      }
      intersection = next
    }
  }
  return intersection && intersection.size > 0 ? [...intersection] : ["__restricted__"]
}

/** Runtime check — keep in sync with salesRestrictedProductFilter Skeem logic. */
export function isProductSalesRestrictedForCustomer(
  product: {
    product_group?: {
      product_group_master?: IProductGroupMasterRestriction
    }
  },
  customerId?: string,
): boolean {
  const pgm = product.product_group?.product_group_master
  if (!pgm?.sales_restricted) {
    return false
  }
  if (!customerId) {
    return true
  }
  return !pgm.allowed_sales_customers?.some(c => c.id === customerId)
}

export function isProductMasterVisibleToCustomer(
  master: {
    customers: { id: string }[]
    products: IProductWithPgm[]
  },
  customerId?: string,
): boolean {
  // NOPL masters are only visible to their assigned customers
  if (master.customers.length > 0) {
    return customerId ? master.customers.some(c => c.id === customerId) : false
  }
  const products = productMasterSearchProducts(master.products)
  // Anonymous visitors see the same "public" masters as Algolia's customerIds:none —
  // anything that isn't sales-restricted. Logged-in customers additionally pass via
  // the sales-restriction bypass list.
  return !products.some(p => isProductSalesRestrictedForCustomer(p, customerId))
}
